import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { VerifyOtpCustomer } from '../../../api/authApi';
import { updateUser } from '../../../api/userApi';
import Cookies from 'js-cookie';

// Interfaces
interface AuthOtpState {
    isOtpAuthenticated: boolean;
    loading: boolean;
    accessToken: string | null;
    refreshToken: string | null;
    error: string | null;
}

interface OtpPayload {
    phoneNumber: string;
    otpCode: string;
}

interface OtpResponse {
    access: string;
    refresh: string;
    status: number;
}

interface ErrorDetail {
    code: string;
    detail: string;
    attr: string | null;
}

interface StructuredErrorResponse {
    type: 'validation_error' | 'server_error';
    errors: ErrorDetail[];
}

interface GenericErrorResponse {
    error?: string;
}

// Initial state
const initialState: AuthOtpState = {
    isOtpAuthenticated: false,
    loading: false,
    accessToken: null,
    refreshToken: null,
    error: null
};

// Async thunk
export const verifyOtp = createAsyncThunk<
    OtpResponse,
    OtpPayload,
    { rejectValue: string }
>(
    'otp/verify',
    async (payload: OtpPayload, thunkAPI) => {
        try {


            const result = await VerifyOtpCustomer(
                payload.phoneNumber,
                payload.otpCode,
            );

            // Success - status 200
            if (result.status === 200 && result.success) {
                const successData = result.data as any;
                localStorage.setItem("isAuthenticated", "true");
                sessionStorage.removeItem("otpAllowed");

                const currentUserId = localStorage.getItem('currentUserId');
                if (currentUserId) {
                    try {
                        const updateResult = await updateUser(currentUserId, { phone_number: payload.phoneNumber });
                        if (updateResult.success) {
                            const userInfo = localStorage.getItem('user_info');
                            if (userInfo) {
                                const parsed = JSON.parse(userInfo);
                                parsed.phone_number = payload.phoneNumber;
                                localStorage.setItem('user_info', JSON.stringify(parsed));
                            }
                            localStorage.removeItem('currentUserId');
                            localStorage.removeItem('needsPhoneNumber');
                        }
                    } catch (err) {
                        console.error('Failed to update user phone number:', err);
                    }
                }

                const access = successData.access || successData.access_token;
                const refresh = successData.refresh || successData.refresh_token;

                // Store tokens in cookies
                if (access) {
                    document.cookie = `accessToken=${access};path=/;max-age=${7 * 24 * 60 * 60};Secure;SameSite=Strict`;
                }
                if (refresh) {
                    document.cookie = `refreshToken=${refresh};path=/;max-age=${30 * 24 * 60 * 60};Secure;SameSite=Strict`;
                }

                return {
                    access: access,
                    refresh: refresh,
                    status: result.status
                };
            }

            // Error handling
            const errorData = result.data;

            // Structured error
            if (errorData && typeof errorData === 'object' && 'type' in errorData && 'errors' in errorData) {
                const structuredError = errorData as StructuredErrorResponse;
                const firstError = structuredError.errors[0];
                return thunkAPI.rejectWithValue(
                    firstError?.detail || 'Xəta baş verdi'
                );
            }

            // Generic error
            if (errorData && typeof errorData === 'object' && 'error' in errorData) {
                const genericError = errorData as GenericErrorResponse;
                return thunkAPI.rejectWithValue(
                    genericError.error || 'Xəta baş verdi'
                );
            }

            // Network error
            if (result.status === 0) {
                return thunkAPI.rejectWithValue('Şəbəkə xətası - cavab alınmadı');
            }

            // Default error
            return thunkAPI.rejectWithValue('Gözlənilməz xəta baş verdi');

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'OTP doğrulama uğursuzdur';
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

// Slice with reducers
const authOtpSlice = createSlice({
    name: '  authOtp',
    initialState,
    reducers: {
        clearOtpError: (state) => {
            state.error = null;
        },
        resetOtpState: (state) => {
            state.loading = false;
            state.isOtpAuthenticated = false;
            state.accessToken = null;
            state.refreshToken = null;
            state.error = null;
        },
        logout: (state) => {
            state.isOtpAuthenticated = false;
            state.accessToken = null;
            state.refreshToken = null;
            state.error = null;
            localStorage.removeItem("isAuthenticated");
            localStorage.removeItem('user_info');

            // Clear cookies
            Cookies.remove('accessToken');
            Cookies.remove('refreshToken');
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(verifyOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifyOtp.fulfilled, (state, action: PayloadAction<OtpResponse>) => {
                state.loading = false;
                state.isOtpAuthenticated = true;
                state.accessToken = action.payload.access;
                state.refreshToken = action.payload.refresh;
                state.error = null;
            })
            .addCase(verifyOtp.rejected, (state, action) => {
                state.loading = false;
                state.isOtpAuthenticated = false;
                state.accessToken = null;
                state.refreshToken = null;
                state.error = action.payload || 'OTP doğrulama uğursuz oldu';
            });
    }
});

export const { clearOtpError, resetOtpState, logout } = authOtpSlice.actions;
export default authOtpSlice.reducer;